import React, { useMemo, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client/react';
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Divider,
  FormControl,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import SearchIcon from '@mui/icons-material/Search';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import { useSnackbar } from 'notistack';

import Page from '../Page';
import Loading from '../shared/Loading';
import ServerError from '../error/ServerError';
import DirectUpload from '../shared/DirectUpload';
import { GET_ME } from '../../graphql/me';
import { CREATE_POST, CREATE_POST_REPLY, GET_POSTS } from '../../graphql/post';
import config from '../../config';

const stripHtml = (value = '') => value.replace(/<[^>]*>/g, '').trim();

const formatDateTime = (value) => {
  if (!value) {
    return '';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(date);
};

const getPostBody = (post) => stripHtml(post.htmlContent || post.content || '');

const getPostImage = (post) =>
  post.coverImageUrl || post.coverImages?.[0]?.url || null;

const getPostVideo = (post) => post.videoUrl || post.videos?.[0]?.url || null;

const getPostReplyCount = (post) =>
  post.repliesCount ?? post.replies?.length ?? 0;

const getPostStatusLabel = (post) => {
  if (post.status === 'PRO') {
    return 'Pro';
  }

  if (post.status === 'PREMIUM') {
    return 'Premium';
  }

  if (post.status === 'DRAFT') {
    return 'Draft';
  }

  return 'Public';
};

const defaultPostInput = {
  title: '',
  content: '',
  coverImages: [],
  videos: null,
  status: 'PRO',
};

function CommunityPostCard({
  post,
  replyValue,
  onReplyChange,
  onReplySubmit,
  saving,
}) {
  const image = getPostImage(post);
  const video = getPostVideo(post);
  const body = getPostBody(post);
  const replies = [...(post.replies || [])].sort(
    (a, b) => new Date(a.createdAt || 0) - new Date(b.createdAt || 0)
  );

  return (
    <Card variant="outlined" sx={{ borderColor: '#dfe7ef', borderRadius: 1 }}>
      <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
        <Box sx={{ p: 2.25 }}>
          <Stack direction="row" justifyContent="space-between" spacing={2}>
            <Stack direction="row" spacing={1.5} minWidth={0}>
              <Avatar
                src={post.user?.avatarUrl}
                sx={{ bgcolor: '#111827', height: 42, width: 42 }}
              >
                {post.user?.name?.[0]}
              </Avatar>
              <Box minWidth={0}>
                <Stack
                  direction="row"
                  flexWrap="wrap"
                  gap={1}
                  alignItems="center"
                >
                  <Typography sx={{ fontWeight: 900 }}>
                    {post.user?.name || 'Community member'}
                  </Typography>
                  <Chip
                    label={getPostStatusLabel(post)}
                    size="small"
                    sx={{
                      bgcolor: post.status === 'DRAFT' ? '#f1f5f9' : '#ecfdf5',
                      color: post.status === 'DRAFT' ? '#475569' : '#047857',
                      fontWeight: 800,
                    }}
                  />
                </Stack>
                <Typography sx={{ color: '#64748b', fontSize: 13 }}>
                  {formatDateTime(post.createdAt)}
                </Typography>
              </Box>
            </Stack>
            <Stack direction="row" spacing={2} sx={{ color: '#475569' }}>
              <Stack direction="row" spacing={0.75} alignItems="center">
                <FavoriteBorderIcon fontSize="small" />
                <Typography sx={{ fontWeight: 700 }}>
                  {post.likedCount || 0}
                </Typography>
              </Stack>
              <Stack direction="row" spacing={0.75} alignItems="center">
                <ChatBubbleOutlineIcon fontSize="small" />
                <Typography sx={{ fontWeight: 700 }}>
                  {getPostReplyCount(post)}
                </Typography>
              </Stack>
            </Stack>
          </Stack>

          {post.title && (
            <Typography sx={{ fontSize: 20, fontWeight: 900, mt: 2 }}>
              {post.title}
            </Typography>
          )}
          {body && (
            <Typography
              sx={{ color: '#334155', mt: 1.25, whiteSpace: 'pre-wrap' }}
            >
              {body}
            </Typography>
          )}
          {video ? (
            <Box
              component="video"
              controls
              playsInline
              src={video}
              sx={{
                borderRadius: 1,
                display: 'block',
                maxHeight: 260,
                maxWidth: 520,
                mt: 2,
                objectFit: 'cover',
                width: '100%',
              }}
            />
          ) : image && (
            <Box
              component="img"
              src={image}
              alt={post.title || 'Community post'}
              sx={{
                borderRadius: 1,
                display: 'block',
                maxHeight: 260,
                maxWidth: 520,
                mt: 2,
                objectFit: 'cover',
                width: '100%',
              }}
            />
          )}
        </Box>

        <Divider />

        <Box sx={{ bgcolor: '#f8fafc', p: 2.25 }}>
          <Stack spacing={1.5}>
            {replies.length === 0 ? (
              <Typography sx={{ color: '#64748b', fontSize: 14 }}>
                No comments yet.
              </Typography>
            ) : (
              replies.map((reply) => (
                <Stack direction="row" key={reply._id} spacing={1.25}>
                  <Avatar
                    src={reply.user?.avatarUrl}
                    sx={{ height: 30, width: 30 }}
                  >
                    {reply.user?.name?.[0]}
                  </Avatar>
                  <Box
                    sx={{
                      bgcolor: 'background.paper',
                      border: '1px solid #e2e8f0',
                      borderRadius: 1,
                      px: 1.5,
                      py: 1,
                      width: '100%',
                    }}
                  >
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      spacing={2}
                    >
                      <Typography sx={{ fontSize: 13, fontWeight: 800 }}>
                        {reply.user?.name || 'Member'}
                      </Typography>
                      <Typography sx={{ color: '#64748b', fontSize: 12 }}>
                        {formatDateTime(reply.createdAt)}
                      </Typography>
                    </Stack>
                    <Typography sx={{ color: '#334155', mt: 0.5 }}>
                      {reply.content}
                    </Typography>
                  </Box>
                </Stack>
              ))
            )}
          </Stack>

          <Box component="form" onSubmit={onReplySubmit} sx={{ mt: 2 }}>
            <TextField
              fullWidth
              multiline
              minRows={2}
              onChange={onReplyChange}
              placeholder="Respond as trainer..."
              value={replyValue}
            />
            <Stack direction="row" justifyContent="flex-end" sx={{ mt: 1 }}>
              <Button
                disabled={saving || !replyValue.trim()}
                endIcon={<SendOutlinedIcon />}
                type="submit"
                variant="contained"
              >
                Reply
              </Button>
            </Stack>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}

export default function CommunityBoard() {
  const [searchTerm, setSearchTerm] = useState('');
  const [replyDrafts, setReplyDrafts] = useState({});
  const [savingPostId, setSavingPostId] = useState(null);
  const [postInput, setPostInput] = useState(defaultPostInput);
  const [creatingPost, setCreatingPost] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  const { loading: meLoading, error: meError, data: meData } = useQuery(GET_ME);
  const trainerId = meData?.me?._id;

  const {
    loading: postsLoading,
    error: postsError,
    data: postsData,
    refetch,
  } = useQuery(GET_POSTS, {
    variables: { input: { trainerId, limit: 50 } },
    skip: !trainerId,
  });

  const [createPostReply] = useMutation(CREATE_POST_REPLY, {
    onError: (error) => {
      enqueueSnackbar(`Error: ${error.message}`, { variant: 'error' });
    },
  });
  const [createPost] = useMutation(CREATE_POST, {
    onError: (error) => {
      enqueueSnackbar(`Error: ${error.message}`, { variant: 'error' });
    },
  });

  const posts = useMemo(() => postsData?.posts || [], [postsData?.posts]);
  const filteredPosts = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();

    if (!normalizedSearch) {
      return posts;
    }

    return posts.filter((post) =>
      [
        post.title,
        getPostBody(post),
        post.user?.name,
        ...(post.replies || []).map((reply) => reply.content),
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
        .includes(normalizedSearch)
    );
  }, [posts, searchTerm]);

  const totalComments = posts.reduce(
    (sum, post) => sum + getPostReplyCount(post),
    0
  );
  const postsWithImages = posts.filter((post) => getPostImage(post)).length;

  const handleReplySubmit = async (event, post) => {
    event.preventDefault();
    const content = (replyDrafts[post._id] || '').trim();

    if (!content) {
      return;
    }

    setSavingPostId(post._id);

    try {
      await createPostReply({
        variables: { input: { postId: post._id, content } },
      });
      setReplyDrafts((drafts) => ({ ...drafts, [post._id]: '' }));
      await refetch();
      enqueueSnackbar('Reply posted.', { variant: 'success' });
    } finally {
      setSavingPostId(null);
    }
  };

  const handlePostInputChange = (event) => {
    setPostInput({
      ...postInput,
      [event.target.name]: event.target.value,
    });
  };

  const handleUploadSuccess = (uploadedResponse) => {
    setPostInput((input) => ({
      ...input,
      coverImages: uploadedResponse.image ? [uploadedResponse.image] : [],
      videos: uploadedResponse.videos || null,
    }));
  };

  const handleUploadRemove = () => {
    setPostInput((input) => ({
      ...input,
      coverImages: [],
      videos: null,
    }));
  };

  const handleCreatePost = async (event) => {
    event.preventDefault();

    const content = postInput.content.trim();
    const title = postInput.title.trim();

    if (!content && !title) {
      return;
    }

    setCreatingPost(true);

    try {
      await createPost({
        variables: {
          input: {
            trainerId,
            title: title || content.slice(0, 80),
            content,
            coverImages: postInput.coverImages,
            videos: postInput.videos,
            status: postInput.status,
          },
        },
      });
      setPostInput(defaultPostInput);
      await refetch();
      enqueueSnackbar('Post created.', { variant: 'success' });
    } finally {
      setCreatingPost(false);
    }
  };

  if (meError) {
    return <ServerError message={meError.message} />;
  }

  if (postsError) {
    return <ServerError message={postsError.message} />;
  }

  if (meLoading || postsLoading) {
    return <Loading />;
  }

  return (
    <Page title={`Community Board : ${config.appDisplayName}`}>
      <Container maxWidth={false}>
        <Stack
          alignItems={{ xs: 'flex-start', md: 'center' }}
          direction={{ xs: 'column', md: 'row' }}
          justifyContent="space-between"
          spacing={2}
          sx={{ mb: 3 }}
        >
          <Box>
            <Typography
              sx={{ color: '#0f172a', fontSize: 30, fontWeight: 900 }}
            >
              Community Board
            </Typography>
            <Typography sx={{ color: '#475569', mt: 0.75 }}>
              Read member posts and respond to comments from one shared board.
            </Typography>
          </Box>
          <TextField
            onChange={(event) => setSearchTerm(event.target.value)}
            placeholder="Search posts or comments..."
            size="small"
            sx={{ width: { xs: '100%', md: 320 } }}
            value={searchTerm}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: '#64748b' }} />
                </InputAdornment>
              ),
            }}
          />
        </Stack>

        <Card
          variant="outlined"
          sx={{ borderColor: '#dfe7ef', borderRadius: 1, mb: 3 }}
        >
          <CardContent>
            <Box component="form" onSubmit={handleCreatePost}>
              <Stack spacing={2}>
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Avatar
                    src={meData?.me?.avatarUrl}
                    sx={{ bgcolor: '#111827', height: 38, width: 38 }}
                  >
                    {meData?.me?.name?.[0]}
                  </Avatar>
                  <Box>
                    <Typography sx={{ fontWeight: 900 }}>
                      Create a post
                    </Typography>
                    <Typography sx={{ color: '#64748b', fontSize: 13 }}>
                      Share an update with the community.
                    </Typography>
                  </Box>
                </Stack>

                <TextField
                  fullWidth
                  label="Title"
                  name="title"
                  onChange={handlePostInputChange}
                  value={postInput.title}
                />
                <TextField
                  fullWidth
                  label="Post"
                  multiline
                  name="content"
                  onChange={handlePostInputChange}
                  placeholder="What's happening in the community?"
                  minRows={4}
                  value={postInput.content}
                />
                <Box>
                  <Typography sx={{ fontWeight: 800, mb: 1 }}>
                    Media
                  </Typography>
                  <DirectUpload
                    fileType="video"
                    images={postInput.coverImages}
                    maxFiles={1}
                    onError={(message) =>
                      enqueueSnackbar(`Upload failed: ${message}`, {
                        variant: 'error',
                      })
                    }
                    onRemove={handleUploadRemove}
                    onSuccess={handleUploadSuccess}
                    streaming={true}
                    style={{
                      alignItems: 'center',
                      backgroundColor: '#fff',
                      border: '1px dashed #cbd5e1',
                      borderRadius: 8,
                      display: 'flex',
                      justifyContent: 'center',
                      minHeight: 128,
                    }}
                  />
                </Box>
                <Stack
                  alignItems={{ xs: 'stretch', sm: 'center' }}
                  direction={{ xs: 'column', sm: 'row' }}
                  justifyContent="space-between"
                  spacing={2}
                >
                  <FormControl size="small" sx={{ minWidth: 180 }}>
                    <InputLabel id="community-post-status-label">
                      Visibility
                    </InputLabel>
                    <Select
                      label="Visibility"
                      labelId="community-post-status-label"
                      name="status"
                      onChange={handlePostInputChange}
                      value={postInput.status}
                    >
                      <MenuItem value="PUBLIC">Public</MenuItem>
                      <MenuItem value="PRO">Pro</MenuItem>
                      <MenuItem value="PREMIUM">Premium</MenuItem>
                      <MenuItem value="DRAFT">Draft</MenuItem>
                    </Select>
                  </FormControl>
                  <Button
                    disabled={
                      creatingPost ||
                      (!postInput.title.trim() && !postInput.content.trim())
                    }
                    startIcon={<AddIcon />}
                    type="submit"
                    variant="contained"
                  >
                    Create Post
                  </Button>
                </Stack>
              </Stack>
            </Box>
          </CardContent>
        </Card>

        <Grid container spacing={2.5} sx={{ mb: 3 }}>
          <Grid item xs={12} md={4}>
            <Card
              variant="outlined"
              sx={{ borderColor: '#dfe7ef', borderRadius: 1 }}
            >
              <CardContent>
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <GroupsOutlinedIcon sx={{ color: '#4338ca' }} />
                  <Box>
                    <Typography sx={{ fontSize: 24, fontWeight: 900 }}>
                      {posts.length}
                    </Typography>
                    <Typography sx={{ color: '#64748b' }}>Posts</Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card
              variant="outlined"
              sx={{ borderColor: '#dfe7ef', borderRadius: 1 }}
            >
              <CardContent>
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <ChatBubbleOutlineIcon sx={{ color: '#047857' }} />
                  <Box>
                    <Typography sx={{ fontSize: 24, fontWeight: 900 }}>
                      {totalComments}
                    </Typography>
                    <Typography sx={{ color: '#64748b' }}>Comments</Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card
              variant="outlined"
              sx={{ borderColor: '#dfe7ef', borderRadius: 1 }}
            >
              <CardContent>
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <ImageOutlinedIcon sx={{ color: '#b45309' }} />
                  <Box>
                    <Typography sx={{ fontSize: 24, fontWeight: 900 }}>
                      {postsWithImages}
                    </Typography>
                    <Typography sx={{ color: '#64748b' }}>
                      Media posts
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Stack spacing={2.5}>
          {filteredPosts.length === 0 ? (
            <Card
              variant="outlined"
              sx={{ borderColor: '#dfe7ef', borderRadius: 1 }}
            >
              <CardContent>
                <Typography sx={{ color: '#64748b' }}>
                  No community posts found.
                </Typography>
              </CardContent>
            </Card>
          ) : (
            filteredPosts.map((post) => (
              <CommunityPostCard
                key={post._id}
                onReplyChange={(event) =>
                  setReplyDrafts((drafts) => ({
                    ...drafts,
                    [post._id]: event.target.value,
                  }))
                }
                onReplySubmit={(event) => handleReplySubmit(event, post)}
                post={post}
                replyValue={replyDrafts[post._id] || ''}
                saving={savingPostId === post._id}
              />
            ))
          )}
        </Stack>
      </Container>
    </Page>
  );
}
